import { useEffect, useRef, useState } from "react";
import { Play, Pause, Bookmark, Maximize } from "lucide-react";
import { toast } from "sonner";

const GOLD = "#B8922A";
const INK = "#1A1A18";
const CREAM = "#F5F0E8";

const fmt = (s) => {
  if (!Number.isFinite(s)) return "0:00";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${String(sec).padStart(2, "0")}`;
};

export const LessonPlayer = ({ src, poster, chapters = [], storageKey, onEnded, testId }) => {
  const videoRef = useRef(null);
  const saveTimer = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [current, setCurrent] = useState(0);
  const [resumeAt, setResumeAt] = useState(null);
  const lsKey = `ws_resume_${storageKey}`;

  useEffect(() => {
    const saved = parseFloat(localStorage.getItem(lsKey) || "0");
    if (saved > 10) setResumeAt(saved);
  }, [lsKey]);

  useEffect(() => {
    if (playing) {
      saveTimer.current = setInterval(() => {
        const v = videoRef.current;
        if (v && v.currentTime > 5) localStorage.setItem(lsKey, String(v.currentTime));
      }, 30000);
    }
    return () => clearInterval(saveTimer.current);
  }, [playing, lsKey]);

  const chapterTime = (ch) =>
    ch.timestamp_seconds != null ? ch.timestamp_seconds : (ch.pct || 0) * duration;

  const toggle = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) v.play();
    else v.pause();
  };

  const seekTo = (t) => {
    const v = videoRef.current;
    if (!v) return;
    v.currentTime = Math.min(t, (v.duration || t));
    v.play();
  };

  const onBarClick = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = (e.clientX - rect.left) / rect.width;
    if (duration) seekTo(pct * duration);
  };

  const saveBookmark = () => {
    const v = videoRef.current;
    if (!v) return;
    localStorage.setItem(lsKey, String(v.currentTime));
    toast.success(`Segnalibro salvato al minuto ${fmt(v.currentTime)}`);
  };

  const fullscreen = () => videoRef.current?.requestFullscreen?.();

  return (
    <div className="mt-4" data-testid={`player-${testId}`}>
      <div className="relative border" style={{ borderColor: "#2A2A2A", background: INK }}>
        <video
          ref={videoRef}
          poster={poster}
          preload="metadata"
          playsInline
          className="w-full aspect-video bg-black cursor-pointer"
          data-testid={testId}
          onClick={toggle}
          onPlay={() => setPlaying(true)}
          onPause={() => {
            setPlaying(false);
            const v = videoRef.current;
            if (v && v.currentTime > 5) localStorage.setItem(lsKey, String(v.currentTime));
          }}
          onTimeUpdate={(e) => setCurrent(e.target.currentTime)}
          onLoadedMetadata={(e) => setDuration(e.target.duration)}
          onEnded={() => {
            localStorage.removeItem(lsKey);
            onEnded?.();
          }}
        >
          <source src={src} type="video/mp4" />
        </video>

        {resumeAt != null && (
          <div className="absolute inset-0 flex items-center justify-center px-4" style={{ background: "rgba(26,26,24,0.88)" }} data-testid="resume-popup">
            <div className="text-center max-w-sm">
              <Bookmark size={28} className="mx-auto mb-3" style={{ color: GOLD }} />
              <p className="text-sm mb-5 leading-relaxed" style={{ color: CREAM }}>
                Hai lasciato questa lezione al minuto <strong style={{ color: GOLD }}>{fmt(resumeAt)}</strong> — Riprendi da qui?
              </p>
              <div className="flex gap-3 justify-center">
                <button data-testid="resume-btn"
                  onClick={() => { seekTo(resumeAt); setResumeAt(null); }}
                  className="px-5 py-2.5 text-xs font-bold uppercase tracking-wider transition-opacity hover:opacity-85"
                  style={{ background: GOLD, color: INK }}>
                  Riprendi
                </button>
                <button data-testid="restart-btn"
                  onClick={() => { localStorage.removeItem(lsKey); seekTo(0); setResumeAt(null); }}
                  className="px-5 py-2.5 text-xs font-bold uppercase tracking-wider border transition-colors hover:opacity-85"
                  style={{ borderColor: GOLD, color: CREAM }}>
                  Ricomincia
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="px-3 py-2.5 flex items-center gap-3" style={{ background: INK }}>
          <button onClick={toggle} data-testid={`play-toggle-${testId}`} aria-label={playing ? "Pausa" : "Play"}
            className="shrink-0 transition-opacity hover:opacity-80" style={{ color: CREAM }}>
            {playing ? <Pause size={18} /> : <Play size={18} />}
          </button>

          <div className="relative flex-1 h-5 flex items-center cursor-pointer group" onClick={onBarClick} data-testid={`progress-bar-${testId}`}>
            <div className="w-full h-1.5 group-hover:h-2.5 transition-all" style={{ background: "#3a3a36" }}>
              <div className="h-full" style={{ background: GOLD, width: duration ? `${(current / duration) * 100}%` : "0%" }} />
            </div>
            {duration > 0 && chapters.map((ch, i) => (
              <button key={i} title={`${ch.icon || ""} ${ch.name} — ${fmt(chapterTime(ch))}`}
                data-testid={`chapter-marker-${testId}-${i}`}
                onClick={(e) => { e.stopPropagation(); seekTo(chapterTime(ch)); }}
                className="absolute w-3 h-3 rounded-full -translate-x-1/2 border-2 hover:scale-150 transition-transform"
                style={{ left: `${(chapterTime(ch) / duration) * 100}%`, background: CREAM, borderColor: GOLD, top: "4px" }} />
            ))}
          </div>

          <span className="shrink-0 text-[11px] tabular-nums" style={{ color: CREAM }} data-testid={`time-${testId}`}>
            {fmt(current)} / {fmt(duration)}
          </span>
          <button onClick={saveBookmark} data-testid={`bookmark-btn-${testId}`} title="Salva segnalibro"
            className="shrink-0 transition-opacity hover:opacity-80" style={{ color: GOLD }}>
            <Bookmark size={17} />
          </button>
          <button onClick={fullscreen} aria-label="Schermo intero"
            className="shrink-0 transition-opacity hover:opacity-80" style={{ color: CREAM }}>
            <Maximize size={16} />
          </button>
        </div>
      </div>

      {chapters.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3" data-testid={`chapters-${testId}`}>
          {chapters.map((ch, i) => {
            const t = chapterTime(ch);
            const active = duration > 0 && current >= t &&
              (i === chapters.length - 1 || current < chapterTime(chapters[i + 1]));
            return (
              <button key={i} onClick={() => seekTo(t)} data-testid={`chapter-btn-${testId}-${i}`}
                className="px-2 py-2.5 text-[11px] font-bold uppercase tracking-wide border text-left transition-colors"
                style={active
                  ? { background: GOLD, color: INK, borderColor: GOLD }
                  : { background: INK, color: CREAM, borderColor: "#3a3a36" }}>
                <span className="block text-base mb-0.5">{ch.icon}</span>
                {ch.name}
                <span className="block font-normal normal-case mt-0.5" style={{ color: active ? INK : GOLD }}>
                  {duration ? fmt(t) : "—"}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};
